import React, { useState, useEffect, CSSProperties } from 'react';
import { Category, CategoryType, CategoryAccountType } from '../../types';

interface CategoryFormProps {
  category: Category | null;
  onSave: (data: any) => void;
  onCancel: () => void;
}

const CategoryForm: React.FC<CategoryFormProps> = ({ category, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'expense' as CategoryType,
    account_type: 'personal' as CategoryAccountType,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name,
        description: category.description || '',
        type: category.type,
        account_type: category.account_type,
      });
    }
  }, [category]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validate()) {
      onSave(formData);
    }
  };

  // Define inline styles
  const modalBackdropStyle: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1050,
  };

  const modalStyle: CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '4px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
  };

  const modalHeaderStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    borderBottom: '1px solid #dee2e6',
  };

  const modalTitleStyle: CSSProperties = {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 600,
  };

  const closeButtonStyle: CSSProperties = {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    padding: 0,
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
  };

  const modalBodyStyle: CSSProperties = {
    padding: '1rem',
  };

  const modalFooterStyle: CSSProperties = {
    padding: '1rem',
    borderTop: '1px solid #dee2e6',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
  };

  const formGroupStyle: CSSProperties = {
    marginBottom: '1.25rem',
  };

  const labelStyle: CSSProperties = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 500,
    fontSize: '0.875rem',
  };

  const formControlStyle: CSSProperties = {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '0.875rem',
    fontFamily: 'inherit',
  };

  const errorStyle: CSSProperties = {
    color: '#dc3545',
    fontSize: '0.75rem',
    marginTop: '0.25rem',
  };

  const toggleGroupStyle: CSSProperties = {
    display: 'flex',
    overflow: 'hidden',
    borderRadius: '4px',
    border: '1px solid #ced4da',
  };

  const toggleOptionStyle = (isActive: boolean): CSSProperties => ({
    flex: 1,
    position: 'relative',
    textAlign: 'center',
    padding: '0.75rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: isActive ? '#3498db' : '#f8f9fa',
    color: isActive ? 'white' : 'inherit',
    borderRight: '1px solid #ced4da',
  });

  const toggleInputStyle: CSSProperties = {
    position: 'absolute',
    clip: 'rect(0, 0, 0, 0)',
    pointerEvents: 'none',
  };

  const btnSecondaryStyle: CSSProperties = {
    backgroundColor: '#f8f9fa',
    color: '#212529',
    border: '1px solid #ced4da',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  };

  const btnPrimaryStyle: CSSProperties = {
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  };

  return (
    <div style={modalBackdropStyle}>
      <div style={modalStyle}>
        <div style={modalHeaderStyle}>
          <h3 style={modalTitleStyle}>{category ? 'Edit Category' : 'Create Category'}</h3>
          <button style={closeButtonStyle} onClick={onCancel}>&times;</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div style={modalBodyStyle}>
            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="name">Category Name</label>
              <input
                type="text"
                name="name"
                id="name"
                value={formData.name}
                onChange={handleChange}
                style={formControlStyle}
                placeholder="Enter category name"
              />
              {errors.name && <div style={errorStyle}>{errors.name}</div>}
            </div>
            
            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="description">Description (Optional)</label>
              <textarea
                name="description"
                id="description"
                value={formData.description}
                onChange={handleChange}
                style={formControlStyle}
                placeholder="Enter category description"
                rows={3}
              />
            </div>
            
            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="type">Category Type</label>
              <div style={toggleGroupStyle}>
                <label style={toggleOptionStyle(formData.type === 'expense')}>
                  <input
                    type="radio"
                    name="type"
                    value="expense"
                    checked={formData.type === 'expense'}
                    onChange={handleChange}
                    style={toggleInputStyle}
                  />
                  <span>Expense</span>
                </label>
                <label style={toggleOptionStyle(formData.type === 'income')}>
                  <input
                    type="radio"
                    name="type"
                    value="income"
                    checked={formData.type === 'income'}
                    onChange={handleChange}
                    style={toggleInputStyle}
                  />
                  <span>Income</span>
                </label>
              </div>
            </div>
            
            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="account_type">Account Type</label>
              <div style={toggleGroupStyle}>
                <label style={toggleOptionStyle(formData.account_type === 'personal')}>
                  <input
                    type="radio"
                    name="account_type"
                    value="personal"
                    checked={formData.account_type === 'personal'}
                    onChange={handleChange}
                    style={toggleInputStyle}
                  />
                  <span>Personal</span>
                </label>
                <label style={{...toggleOptionStyle(formData.account_type === 'company'), borderRight: 'none'}}>
                  <input
                    type="radio"
                    name="account_type"
                    value="company"
                    checked={formData.account_type === 'company'}
                    onChange={handleChange}
                    style={toggleInputStyle}
                  />
                  <span>Company</span>
                </label>
              </div>
            </div>
          </div>
          
          <div style={modalFooterStyle}>
            <button type="button" style={btnSecondaryStyle} onClick={onCancel}>Cancel</button>
            <button type="submit" style={btnPrimaryStyle}>Save Category</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryForm;
